/*
 * Keep the last good answer for a read, so a backend restart (about 4 minutes
 * after every deploy) does not turn a full page into "Could not load".
 *
 * On success the result is saved in localStorage. When the request fails and a
 * saved copy exists, the saved copy is returned instead of the error, marked
 * with a hidden `__offline` flag. The page shows it with a small "reconnecting"
 * note and the query polls until the server answers again.
 *
 * Queries using this need `structuralSharing: false`, or React Query copies the
 * value and the hidden flag is lost.
 */

const PREFIX = 'copyfi.lastGood.';

export function isOffline(value: unknown): boolean {
  return !!value && typeof value === 'object' && (value as any).__offline === true;
}

export async function withLastGood<T>(key: string, fetch: () => Promise<T>): Promise<T> {
  try {
    const fresh = await fetch();
    try {
      localStorage.setItem(PREFIX + key, JSON.stringify(fresh));
    } catch {
      // Storage full or blocked: the page still works, only without the fallback.
    }
    return fresh;
  } catch (err) {
    let saved: T | null = null;
    try {
      const raw = localStorage.getItem(PREFIX + key);
      saved = raw ? (JSON.parse(raw) as T) : null;
    } catch {
      saved = null;
    }
    if (saved === null || typeof saved !== 'object') throw err;
    Object.defineProperty(saved, '__offline', { value: true, enumerable: false });
    return saved;
  }
}

/** Poll every 15 s while showing a saved copy, so the page recovers by itself. */
export function pollWhileOffline<T>(query: { state: { data: T | undefined } }): number | false {
  return isOffline(query.state.data) ? 15000 : false;
}
